/**
 * @file src/orbiter/baseFlow/rootParams.js
 * @description Pure per-voice root-parameter hydration — registering a voice's root axes (x/y/z) and
 * its two gain-staging levels from track data. Extracted from createInitializeBaseFlow so BOTH the
 * full base flow AND the audio-only voice session (multi-orbiter secondaries) hydrate params
 * through the SAME code (no forked param path), WITHOUT dragging in the heavy boot graph
 * (Interaction → MIDIController → Main) that the full base flow imports.
 */
import { AXIS_ROTATION_CONSTRAINTS, GAIN_STAGE_CONSTRAINTS } from '../../config/Constants.js';
import { GAIN_STAGE_PARAMS } from '../../audio/gainStageParams.js';
import { linear } from '../../core/Transformations.js';

const ROOT_AXES = ['x', 'y', 'z'];
const identity = (x) => x;

/**
 * The two user gain stages: the input trim ahead of processing and the premix fader after it.
 *
 * Their dB range is FIXED here rather than read from the saved orbiter, because the gain rail
 * draws the fader and the level meter on one shared axis — a per-orbiter range would put the
 * handle and the metering on different scales, and would silently change what a learned MIDI CC
 * maps to. A saved value outside the range is clamped into it.
 */
const GAIN_PARAM_KEYS = GAIN_STAGE_PARAMS;
const { min: GAIN_MIN_DB, max: GAIN_MAX_DB, equilibrium: GAIN_EQUILIBRIUM_DB } = GAIN_STAGE_CONSTRAINTS;

export function initializeRootParams(parameterManager, trackData) {
  const savedParams = trackData?.orbiter?.orbiterParams ?? {};

  GAIN_PARAM_KEYS.forEach((key) => {
    const incoming = savedParams[key] && typeof savedParams[key] === 'object' ? savedParams[key] : {};
    const savedValue = Number(incoming.value ?? incoming.initValue);
    const value = Number.isFinite(savedValue)
      ? Math.max(GAIN_MIN_DB, Math.min(GAIN_MAX_DB, savedValue))
      : GAIN_EQUILIBRIUM_DB;

    // Register AT the equilibrium, then move to the saved level. ParameterManager snapshots a
    // parameter's reset value from whatever it is first registered with, so registering with the
    // saved level would make "reset" mean "back to how this orbiter was saved" instead of unity —
    // and unity is the only meaningful neutral for a gain.
    parameterManager.addParameter(
      key,
      GAIN_EQUILIBRIUM_DB,
      GAIN_MIN_DB,
      GAIN_MAX_DB,
      true,
      'linear',
      linear.inverse,
      linear.forward,
    );
    parameterManager.setRawValue(key, value);
  });
}

export function hydrateRootAxesFromTrack(parameterManager, trackData) {
  if (!parameterManager || !trackData?.orbiter?.orbiterParams) {
    return;
  }

  const axisDefaults = {
    min: AXIS_ROTATION_CONSTRAINTS?.min ?? -180,
    max: AXIS_ROTATION_CONSTRAINTS?.max ?? 180,
    equilibrium: AXIS_ROTATION_CONSTRAINTS?.equilibrium ?? 0,
  };

  ROOT_AXES.forEach((axis) => {
    const paramConfig = trackData.orbiter.orbiterParams[axis];
    if (!paramConfig || typeof paramConfig !== 'object') return;

    const min = Number.isFinite(paramConfig.min) ? Number(paramConfig.min) : axisDefaults.min;
    const max = Number.isFinite(paramConfig.max) ? Number(paramConfig.max) : axisDefaults.max;
    const valueCandidate =
      Number.isFinite(paramConfig.value) ? Number(paramConfig.value) : Number(paramConfig.initValue);
    const value = Number.isFinite(valueCandidate) ? valueCandidate : axisDefaults.equilibrium;

    parameterManager.addParameter(
      axis,
      value,
      min,
      max,
      true,
      paramConfig.scale || 'linear',
      identity,
      identity
    );
    parameterManager.setRawValue(axis, value);
  });
}
